/**
 * Similar cases: describe what you're looking for ("white SUV breaking into cars at night") or start from a report,
 * and see the reports that share the most with it — with why each matched, the words in its narrative, and a map.
 */
import { useEffect, useMemo, useState, type FormEvent } from 'react';
import { api } from '../shared/api.ts';
import { useRouter } from '../shared/router.tsx';
import { SlippyMap } from '../shared/SlippyMap.tsx';
import { Badge, ErrorBox, PageHeader, Spinner } from '../shared/ui.tsx';
import { reportsBase } from './ReportWizard.tsx';

const RANGES: [string, string][] = [
  ['90', 'Last 90 days'],
  ['365', 'Last year'],
  ['730', 'Last 2 years'],
  ['1825', 'Last 5 years'],
  ['all', 'All years'],
];
const STRENGTH: Record<string, [string, 'bad' | 'warn' | 'neutral']> = { strong: ['Strong match', 'bad'], good: ['Good match', 'warn'], some: ['Some in common', 'neutral'] };
const KIND_TONE: Record<string, 'bad' | 'info' | 'neutral' | 'warn' | 'ok'> = { strong: 'bad', vehicle: 'info', mo: 'warn', target: 'warn', item: 'warn', weapon: 'bad', clothing: 'info', evidence: 'neutral', words: 'neutral', offense: 'neutral', place: 'ok', time: 'ok' };
const EXAMPLES = ['white SUV breaking into cars at night', 'catalytic converters cut off church vans', 'pry marks on the back door, guns taken', 'man in a ski mask robbed the store with a knife'];

/** "[[white]] [[SUV]]" from the server → highlighted words. */
function Snippet({ s }: { s: string }) {
  if (!s) return null;
  const parts = s.split(/\[\[|\]\]/);
  return (
    <p className="sim-snip">
      {parts.map((p, i) => (i % 2 ? <mark key={i}>{p}</mark> : <span key={i}>{p}</span>))}
    </p>
  );
}

export function SimilarCasesPage() {
  const { navigate } = useRouter();
  const qs = new URLSearchParams(location.search);
  const [from, setFrom] = useState(qs.get('from'));
  const [q, setQ] = useState(qs.get('q') ?? '');
  const [since, setSince] = useState(qs.get('since') ?? '730');
  const [within, setWithin] = useState('');
  const [sameOffense, setSameOffense] = useState(false);
  const [data, setData] = useState<any>(null);
  const [error, setError] = useState<unknown>(null);
  const [busy, setBusy] = useState(false);
  const [showMap, setShowMap] = useState(true);
  const base = reportsBase();

  const run = async (over?: { from?: string | null; q?: string }) => {
    const f = over && 'from' in over ? over.from : from;
    const t = over?.q ?? q;
    if (!f && t.trim().length < 3) return;
    setBusy(true);
    setError(null);
    try {
      const p = new URLSearchParams({ since });
      if (f) p.set('from', f);
      if (t.trim()) p.set('q', t.trim());
      if (within && f) p.set('within', within);
      if (sameOffense && f) p.set('offense', 'same');
      setData(await api.get<any>(`/api/reports/similar?${p}`));
      history.replaceState(null, '', `${location.pathname}?${new URLSearchParams({ ...(f ? { from: f } : {}), ...(t.trim() ? { q: t.trim() } : {}), since })}`);
    } catch (e) {
      setError(e);
    } finally {
      setBusy(false);
    }
  };
  // Opened from a report (or a saved link): search right away.
  useEffect(() => {
    if (from || q) void run();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);
  const submit = (e: FormEvent) => (e.preventDefault(), void run());
  const markers = useMemo(
    () => (data?.results ?? []).filter((x: any) => x.lat != null).map((x: any) => ({ id: x.id, lat: x.lat, lng: x.lng, color: x.strength === 'strong' ? '#b42318' : x.strength === 'good' ? '#b54708' : '#475467', title: `${x.caseNumber} · ${x.offense ?? ''}`, dot: x.strength === 'some' })),
    [data],
  );

  return (
    <div className="ops stack sim-page">
      <PageHeader
        crumb="Reports"
        title="Similar cases"
        sub="Find reports that look like the same people at work: the same kind of vehicle, the same way in, the same things taken, the same area."
        actions={
          data?.results?.length ? (
            <button className="btn no-print" onClick={() => window.print()}>
              Print list
            </button>
          ) : undefined
        }
      />
      <form className="card stack sim-form no-print" onSubmit={submit}>
        {from && (
          <div className="row gap-sm wrap">
            <Badge tone="info">Starting from report {data?.seed?.caseNumber ?? '…'}</Badge>
            <button type="button" className="btn link small" onClick={() => (setFrom(null), setData(null))}>
              Search by description only
            </button>
          </div>
        )}
        <label>
          {from ? 'Anything to add (optional)' : 'Describe what you’re looking for'}
          <textarea rows={2} value={q} onChange={(e) => setQ(e.target.value)} placeholder={from ? 'e.g. also wearing gloves' : 'e.g. white SUV breaking into cars at night'} />
        </label>
        {!from && (
          <div className="row gap-sm wrap small">
            <span className="muted">Try:</span>
            {EXAMPLES.map((x) => (
              <button key={x} type="button" className="btn link small" onClick={() => (setQ(x), void run({ q: x }))}>
                {x}
              </button>
            ))}
          </div>
        )}
        <div className="row gap-sm wrap" style={{ alignItems: 'end' }}>
          <label style={{ maxWidth: 180 }}>
            When
            <select value={since} onChange={(e) => setSince(e.target.value)}>
              {RANGES.map(([k, l]) => (
                <option key={k} value={k}>
                  {l}
                </option>
              ))}
            </select>
          </label>
          {from && (
            <>
              <label style={{ maxWidth: 180 }}>
                How far away
                <select value={within} onChange={(e) => setWithin(e.target.value)}>
                  <option value="">Anywhere</option>
                  {['1', '2', '5', '10', '25'].map((m) => (
                    <option key={m} value={m}>
                      Within {m} mi
                    </option>
                  ))}
                </select>
              </label>
              <label className="check">
                <input type="checkbox" checked={sameOffense} onChange={(e) => setSameOffense(e.target.checked)} /> Same offense only
              </label>
            </>
          )}
          <button className="btn primary" disabled={busy}>
            {busy ? 'Searching…' : 'Find similar cases'}
          </button>
        </div>
      </form>
      <ErrorBox error={error} />
      {busy && !data && <Spinner />}
      {data && (
        <>
          <div className="sim-looking small">
            <span className="muted">Looking for: </span>
            {data.seed.lookingFor.map((x: string) => (
              <Badge key={x} tone="info">
                {x}
              </Badge>
            ))}{' '}
            {data.seed.words.length > 0 && <span className="muted">and words like “{data.seed.words.slice(0, 8).join('”, “')}”</span>}
            {!data.seed.lookingFor.length && !data.seed.words.length && <span className="muted">nothing specific — try describing the vehicle, how they got in, or what was taken.</span>}
          </div>
          <div className="row between wrap">
            <h2 className="sim-count">
              {data.results.length ? `${data.results.length} similar ${data.results.length === 1 ? 'report' : 'reports'}` : 'No similar reports'}
              <span className="small muted"> · out of {data.searched.toLocaleString()} searched</span>
            </h2>
            {markers.length > 0 && (
              <button type="button" className="btn sm ghost no-print" onClick={() => setShowMap(!showMap)}>
                {showMap ? 'Hide map' : 'Show map'}
              </button>
            )}
          </div>
          {showMap && markers.length > 0 && (
            <div className="card sim-map no-print">
              <SlippyMap height={320} markers={markers} fitKey={markers.map((m: any) => m.id).join(',')} ariaLabel="Where the similar cases happened" onMarkerClick={(id) => navigate(`${base}/${id}`)} />
            </div>
          )}
          <ol className="sim-list">
            {data.results.map((x: any) => (
              <li key={x.id} className="card sim-item">
                <button type="button" className="sim-open" onClick={() => navigate(`${base}/${x.id}`)}>
                  <div className="row between wrap gap-sm">
                    <div>
                      <strong className="mono">{x.caseNumber}</strong> · <strong>{x.offense ?? 'Report'}</strong>
                      <div className="small muted">
                        {x.occurredAt ? new Date(x.occurredAt).toLocaleString('en-US', { weekday: 'short', month: 'short', day: 'numeric', year: 'numeric', hour: 'numeric', minute: '2-digit' }) : 'Date unknown'}
                        {x.location ? ` · ${x.location}` : ''} · {x.author}
                      </div>
                    </div>
                    <div className="row gap-sm">
                      {x.cleared ? <Badge tone="ok">Cleared</Badge> : <Badge>Open</Badge>}
                      <Badge tone={STRENGTH[x.strength]![1]}>{STRENGTH[x.strength]![0]}</Badge>
                    </div>
                  </div>
                  <div className="sim-why">
                    {x.reasons.map((r: any, i: number) => (
                      <Badge key={i} tone={KIND_TONE[r.kind] ?? 'neutral'}>
                        {r.label}
                      </Badge>
                    ))}
                  </div>
                  <Snippet s={x.snippet} />
                </button>
              </li>
            ))}
          </ol>
        </>
      )}
    </div>
  );
}
